import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, DonutChart, LineChart, Text, Title, Button } from '@tremor/react';
import { FaceSmileIcon, ArrowPathIcon, ChartBarIcon, VideoCameraIcon, InformationCircleIcon } from '@heroicons/react/24/outline';

// ─── helpers ─────────────────────────────────────────────────────────────────

function getEmotionColor(emotion) {
    const map = { happiness: 'emerald', sadness: 'blue', anger: 'red', fear: 'violet', surprise: 'orange', disgust: 'lime', neutral: 'gray' };
    return map[emotion?.toLowerCase()] || 'gray';
}

function getEmotionHexColor(emotion) {
    const map = { happiness: '#10b981', sadness: '#3b82f6', anger: '#ef4444', fear: '#8b5cf6', surprise: '#f97316', disgust: '#65a30d', neutral: '#6b7280' };
    return map[emotion?.toLowerCase()] || '#6b7280';
}

function getEmotionLabel(emotion) {
    if (!emotion) return 'Unknown';
    const map = { happiness: 'Happiness', sadness: 'Sadness', anger: 'Anger', fear: 'Fear', surprise: 'Surprise', disgust: 'Disgust', neutral: 'Neutral' };
    return map[emotion.toLowerCase()] || (emotion.charAt(0).toUpperCase() + emotion.slice(1));
}

function formatTimestamp(seconds) {
    if (!seconds) return '0:00';
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
}

function translateActionUnit(au) {
    if (!au) return au;
    const code = au.toUpperCase();
    const names = {
        AU1: 'Inner Brow Raiser', AU01: 'Inner Brow Raiser',
        AU2: 'Outer Brow Raiser', AU02: 'Outer Brow Raiser',
        AU4: 'Brow Lowerer', AU04: 'Brow Lowerer',
        AU5: 'Upper Lid Raiser', AU05: 'Upper Lid Raiser',
        AU6: 'Cheek Raiser', AU06: 'Cheek Raiser',
        AU7: 'Lid Tightener', AU07: 'Lid Tightener',
        AU9: 'Nose Wrinkler', AU09: 'Nose Wrinkler',
        AU10: 'Upper Lip Raiser', AU11: 'Nasolabial Deepener',
        AU12: 'Lip Corner Puller', AU14: 'Dimpler',
        AU15: 'Lip Corner Depressor', AU17: 'Chin Raiser',
        AU20: 'Lip Stretcher', AU23: 'Lip Tightener',
        AU25: 'Lips Part', AU26: 'Jaw Drop', AU45: 'Blink',
    };
    const friendly = names[code];
    return friendly ? `${code} - ${friendly}` : au;
}

function getActionUnitCode(category) {
    return typeof category === 'string' && category.includes(' - ') ? category.split(' - ')[0] : category;
}

function getActionUnitFriendlyName(category) {
    if (typeof category === 'string' && category.includes(' - ')) {
        const parts = category.split(' - ');
        return parts.length >= 2 ? parts.slice(1).join(' - ') : null;
    }
    return null;
}

function normalizeAuValue(value) {
    if (typeof value === 'number') return value <= 1 ? value * 100 : value;
    if (typeof value === 'string') {
        const n = parseFloat(value);
        if (!isNaN(n)) return n <= 1 ? n * 100 : n;
        if (value === 'true' || value === '1') return 100;
        return 0;
    }
    if (typeof value === 'boolean') return value ? 100 : 0;
    if (typeof value === 'object' && value !== null) {
        if (value.intensity !== undefined) {
            const n = parseFloat(value.intensity);
            return !isNaN(n) ? (Math.abs(n) <= 1 ? Math.abs(n) * 100 : Math.abs(n)) : (value.activated ? 100 : 0);
        }
        if (value.activated !== undefined) return value.activated ? 100 : 0;
    }
    return 0;
}

// ─── CustomTooltip (hover with friendly name) ─────────────────────────────────

function CustomTooltip({ children, tooltip }) {
    const [show, setShow] = useState(false);
    return (
        <div className="relative" onMouseEnter={() => setShow(true)} onMouseLeave={() => setShow(false)}>
            {children}
            {show && tooltip && (
                <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 px-3 py-2 bg-gray-800 text-white text-xs rounded shadow-lg z-50 w-64 max-w-sm break-words">
                    {tooltip}
                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-b-gray-800"></div>
                </div>
            )}
        </div>
    );
}

// ─── Status badge ──────────────────────────────────────────────────────────────

const STATUS_BADGE = {
    queued:              { bg: 'bg-slate-100',  text: 'text-slate-600',  labelKey: 'Questionnaires.VideoEmotion.StatusQueued' },
    processing:          { bg: 'bg-amber-100',  text: 'text-amber-700',  labelKey: 'Questionnaires.VideoEmotion.StatusProcessing' },
    pending_webhook:     { bg: 'bg-amber-100',  text: 'text-amber-700',  labelKey: 'Questionnaires.VideoEmotion.StatusAnalysing' },
    completed:           { bg: 'bg-emerald-100',text: 'text-emerald-700',labelKey: 'Questionnaires.VideoEmotion.StatusDone' },
    failed:              { bg: 'bg-red-100',    text: 'text-red-600',    labelKey: 'Questionnaires.VideoEmotion.StatusFailed' },
    service_unavailable: { bg: 'bg-orange-100', text: 'text-orange-700', labelKey: 'Questionnaires.VideoEmotion.StatusServiceUnavailable' },
};

function StatusBadge({ status }) {
    const { t } = useTranslation();
    if (!status) return null;
    const cfg = STATUS_BADGE[status] ?? { bg: 'bg-slate-100', text: 'text-slate-600', labelKey: null };
    return (
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${cfg.bg} ${cfg.text}`}>
            {cfg.labelKey ? t(cfg.labelKey) : status}
        </span>
    );
}

// ─── View button ───────────────────────────────────────────────────────────────

function ViewBtn({ active, onClick, icon: Icon, children }) {
    return (
        <button
            onClick={onClick}
            className={`flex items-center gap-1 text-xs px-3 py-1.5 rounded border transition-colors ${
                active
                    ? 'bg-iavisus-100 text-white border-iavisus-100'
                    : 'bg-gray-100 text-gray-700 border-gray-200 hover:bg-iavisus-100 hover:text-white hover:border-iavisus-100'
            }`}
        >
            {Icon && <Icon className="h-3.5 w-3.5" />}
            {children}
        </button>
    );
}

// ─── Per-segment three-view panel ─────────────────────────────────────────────

const STALE_MS = 10 * 60 * 1000;

function SegmentEmotionPanel({ segment, index }) {
    const { t } = useTranslation();
    const [view, setView] = useState('overall');
    const [timeInterval, setTimeInterval] = useState(3);
    const [tooltipInfo, setTooltipInfo] = useState(null);
    const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

    const status = segment.video_emotion_status;
    const result = segment.video_emotion_result;

    const isStale = status === 'pending_webhook' &&
        segment.video_emotion_started_at &&
        Date.now() - new Date(segment.video_emotion_started_at).getTime() > STALE_MS;

    const isPending = ['queued', 'processing', 'pending_webhook'].includes(status) && !isStale;

    // ── pending ──
    if (isPending) {
        return (
            <div className="py-10 flex flex-col items-center gap-3 text-gray-400">
                <ArrowPathIcon className="h-6 w-6 animate-spin" />
                <Text className="text-sm">{t('Questionnaires.VideoEmotion.Processing')}</Text>
                <StatusBadge status={status} />
            </div>
        );
    }

    // ── stale / failed / no-face / no-recording ──
    if (!status && !segment.has_person_recording) {
        return <Text className="text-sm text-gray-400 py-8 text-center">{t('Questionnaires.VideoEmotion.ErrorNoCameraRecording')}</Text>;
    }

    if (status === 'failed' || status === 'service_unavailable' || isStale) {
        return (
            <div className="py-8 text-center">
                <Text className="text-sm text-red-400">
                    {isStale ? t('Questionnaires.VideoEmotion.ErrorWebhookNotReceived') :
                     status === 'service_unavailable' ? t('Questionnaires.VideoEmotion.ErrorServiceUnavailable') :
                     t('Questionnaires.VideoEmotion.ErrorAnalysisFailed')}
                </Text>
            </div>
        );
    }

    if (!result) {
        return <Text className="text-sm text-gray-400 py-8 text-center">{t('Questionnaires.VideoEmotion.ErrorNoAnalysisData')}</Text>;
    }

    const frames = result.frames ? Object.values(result.frames) : [];
    const summary = result.summary || {};
    const dominantEmotion = summary.dominant_emotion;
    const standardEmotions = ['happiness', 'sadness', 'anger', 'fear', 'surprise', 'disgust', 'neutral'];

    // ── overall data ──
    const emotionCounts = Object.fromEntries(standardEmotions.map(e => [e, 0]));
    frames.forEach(frame => {
        if (!frame.emotion_scores) return;
        let max = 0, dominant = null;
        Object.entries(frame.emotion_scores).forEach(([em, sc]) => {
            if (sc > max) { max = sc; dominant = em.toLowerCase(); }
        });
        if (dominant && standardEmotions.includes(dominant)) emotionCounts[dominant]++;
    });
    const totalFrames = Object.values(emotionCounts).reduce((s, c) => s + c, 0);
    const donutData = standardEmotions.map(em => ({
        name: getEmotionLabel(em),
        value: totalFrames > 0 ? Math.round((emotionCounts[em] / totalFrames) * 100) : 0,
        emotion: em,
    }));

    // ── timeline chart data ──
    function buildIntervalData(intervalSize) {
        const byKey = new Map();
        frames.forEach(frame => {
            const ts = parseFloat(frame.timestamp || 0);
            const key = Math.floor(ts / intervalSize) * intervalSize;
            if (!byKey.has(key)) byKey.set(key, { ts: key, frames: [] });
            byKey.get(key).frames.push(frame);
        });
        return Array.from(byKey.values())
            .sort((a, b) => a.ts - b.ts)
            .map(({ ts, frames: frs }) => {
                const row = { timestamp: formatTimestamp(ts) };
                const avgs = Object.fromEntries(standardEmotions.map(e => [e, 0]));
                frs.forEach(f => {
                    standardEmotions.forEach(e => { avgs[e] += ((f.emotion_scores || {})[e] || 0); });
                });
                standardEmotions.forEach(e => {
                    row[getEmotionLabel(e)] = Math.round((avgs[e] / frs.length) * 100);
                });
                return row;
            });
    }

    // ── action units data ──
    function buildAuData(intervalSize) {
        const byKey = new Map();
        frames.forEach(frame => {
            const ts = parseFloat(frame.timestamp || 0);
            const key = Math.floor(ts / intervalSize) * intervalSize;
            if (!byKey.has(key)) byKey.set(key, { ts: key, frames: [] });
            byKey.get(key).frames.push(frame);
        });
        return Array.from(byKey.values())
            .sort((a, b) => a.ts - b.ts)
            .map(({ ts, frames: frs }) => {
                const row = { timestamp: formatTimestamp(ts) };
                const auKeys = new Set();
                frs.forEach(f => Object.keys(f.action_units || {}).forEach(au => auKeys.add(au)));
                auKeys.forEach(au => {
                    let sum = 0, cnt = 0;
                    frs.forEach(f => {
                        const v = (f.action_units || {})[au];
                        if (v !== undefined && v !== null) { sum += normalizeAuValue(v); cnt++; }
                    });
                    if (cnt > 0) row[translateActionUnit(au)] = Math.round(sum / cnt);
                });
                return row;
            });
    }

    const auCategories = (() => {
        const auKeys = new Set();
        frames.forEach(f => Object.keys(f.action_units || {}).forEach(au => auKeys.add(au)));
        return Array.from(auKeys).slice(0, 15).map(translateActionUnit);
    })();

    const AU_COLORS = ["#3b82f6","#ef4444","#10b981","#8b5cf6","#f59e0b","#06b6d4","#ec4899","#84cc16","#6b7280","#d946ef","#6366f1","#f43f5e","#f97316","#14b8a6","#a855f7"];
    const AU_TREMOR_COLORS = ["blue","red","emerald","violet","amber","cyan","pink","lime","gray","fuchsia","indigo","rose","orange","teal","purple"];

    // ── floating tooltip overlay ──
    const FloatingTooltip = tooltipInfo ? (() => {
        const W = 260, H = 120, margin = 12;
        const vw = window.innerWidth, vh = window.innerHeight;
        let left, top;
        left = mousePos.x + 14;
        if (left + W > vw - margin) left = mousePos.x - W - 14;
        top = mousePos.y - H - 14;
        if (top < margin) top = mousePos.y + 14;
        return (
            <div
                className="fixed z-50 p-3 bg-white border border-gray-300 rounded-lg shadow-lg text-xs min-w-52"
                style={{ left, top }}
            >
                <div className="font-semibold text-center text-blue-600 mb-2">{tooltipInfo.timestamp}</div>
                <div className="space-y-1">
                    {Object.entries(tooltipInfo.data)
                        .sort(([, a], [, b]) => b - a)
                        .map(([k, v]) => (
                            <div key={k} className="flex justify-between">
                                <span className="text-gray-500 truncate mr-2">{k}</span>
                                <span className="font-medium text-gray-800">{v}%</span>
                            </div>
                        ))}
                </div>
            </div>
        );
    })() : null;

    function makeChartMouseHandlers(chartData) {
        return {
            onMouseMove(e) {
                const rect = e.currentTarget.getBoundingClientRect();
                const x = e.clientX - rect.left;
                if (x < 48) { setTooltipInfo(null); return; }
                setMousePos({ x: e.clientX, y: e.clientY });
                const idx = Math.min(Math.max(0, Math.floor(((x - 48) / (rect.width - 48)) * chartData.length)), chartData.length - 1);
                const pt = chartData[idx];
                if (pt) {
                    setTooltipInfo({
                        timestamp: pt.timestamp,
                        data: Object.fromEntries(
                            Object.entries(pt).filter(([k, v]) => k !== 'timestamp' && typeof v === 'number' && !isNaN(v))
                        ),
                    });
                }
            },
            onMouseLeave() { setTooltipInfo(null); },
        };
    }

    const GranularityPicker = () => (
        <div className="flex items-center gap-1">
            <Text className="text-xs text-gray-500 mr-1 self-center">{t('Questionnaires.VideoEmotion.Granularity')}</Text>
            {[1, 3, 5, 10].map(n => (
                <button
                    key={n}
                    onClick={() => setTimeInterval(n)}
                    className={`text-xs px-2 py-1 rounded border transition-colors ${
                        timeInterval === n
                            ? 'bg-iavisus-100 text-white border-iavisus-100'
                            : 'bg-gray-100 text-gray-700 border-gray-200 hover:bg-iavisus-100 hover:text-white hover:border-iavisus-100'
                    }`}
                >{n}s</button>
            ))}
        </div>
    );

    return (
        <div>
            {FloatingTooltip}

            {/* Status + view switcher */}
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                <StatusBadge status={status} />
                {frames.length > 0 && (
                    <div className="flex gap-1">
                        <ViewBtn active={view === 'overall'} onClick={() => setView('overall')}>{t('Questionnaires.VideoEmotion.ViewOverall')}</ViewBtn>
                        <ViewBtn active={view === 'timeline'} onClick={() => setView('timeline')} icon={ChartBarIcon}>{t('Questionnaires.VideoEmotion.ViewTimeline')}</ViewBtn>
                        <ViewBtn active={view === 'frames'} onClick={() => setView('frames')} icon={VideoCameraIcon}>{t('Questionnaires.VideoEmotion.ViewActionUnits')}</ViewBtn>
                    </div>
                )}
            </div>

            {/* ── Overall ── */}
            {view === 'overall' && (
                <>
                    {dominantEmotion && (
                        <p className="text-lg font-semibold text-gray-800 mb-3">{getEmotionLabel(dominantEmotion)}</p>
                    )}

                    {donutData.every(d => d.value === 0) ? (
                        <Text className="text-sm text-gray-400 py-6 text-center">{t('Questionnaires.VideoEmotion.ErrorNoFaceDetected')}</Text>
                    ) : (
                        <>
                            <DonutChart
                                className="mt-2 h-56"
                                data={donutData}
                                category="value"
                                index="name"
                                colors={donutData.map(d => getEmotionColor(d.emotion))}
                                variant="pie"
                                valueFormatter={v => `${v}%`}
                                showAnimation
                                showLegend={false}
                                showTooltip
                            />
                            <div className="mt-4 flex flex-wrap gap-3 justify-center">
                                {donutData.map(d => (
                                    <div key={d.emotion} className="flex items-center gap-1.5">
                                        <div className="w-3 h-3 rounded" style={{ backgroundColor: getEmotionHexColor(d.emotion) }} />
                                        <Text className="text-sm text-gray-700">{d.name} ({d.value}%)</Text>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </>
            )}

            {/* ── Timeline ── */}
            {view === 'timeline' && frames.length > 0 && (() => {
                const data = buildIntervalData(timeInterval);
                const handlers = makeChartMouseHandlers(data);
                return (
                    <div>
                        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                            <Text className="text-xs text-gray-500">{t('Questionnaires.VideoEmotion.ChartEmotionScores')}</Text>
                            <GranularityPicker />
                        </div>
                        <div className="relative overflow-visible" {...handlers}>
                            <LineChart
                                className="h-56"
                                data={data}
                                index="timestamp"
                                categories={standardEmotions.map(getEmotionLabel)}
                                colors={["emerald","blue","red","violet","orange","lime","gray"]}
                                valueFormatter={v => `${v}%`}
                                yAxisWidth={48}
                                showLegend={false}
                                showGridLines
                                showTooltip={false}
                                connectNulls
                                curveType="monotone"
                            />
                        </div>
                        <div className="mt-4 flex flex-wrap gap-3 justify-center">
                            {[
                                { em: 'happiness', color: '#10b981' }, { em: 'sadness', color: '#3b82f6' },
                                { em: 'anger', color: '#ef4444' }, { em: 'fear', color: '#8b5cf6' },
                                { em: 'surprise', color: '#f97316' }, { em: 'disgust', color: '#65a30d' },
                                { em: 'neutral', color: '#6b7280' },
                            ].map(({ em, color }) => (
                                <div key={em} className="flex items-center gap-1.5">
                                    <div className="w-3 h-3 rounded" style={{ backgroundColor: color }} />
                                    <Text className="text-sm text-gray-700">{getEmotionLabel(em)}</Text>
                                </div>
                            ))}
                        </div>
                    </div>
                );
            })()}

            {/* ── Action Units ── */}
            {view === 'frames' && frames.length > 0 && (() => {
                const data = buildAuData(timeInterval);
                const handlers = makeChartMouseHandlers(data);

                if (auCategories.length === 0) {
                    return <Text className="text-sm text-yellow-600 py-4 text-center">{t('Questionnaires.VideoEmotion.ErrorNoActionUnitData')}</Text>;
                }

                return (
                    <div>
                        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                            <Text className="text-xs text-gray-500">{t('Questionnaires.VideoEmotion.ChartActionUnitIntensity')}</Text>
                            <GranularityPicker />
                        </div>
                        <div className="relative overflow-visible" {...handlers}>
                            <LineChart
                                className="h-56"
                                data={data}
                                index="timestamp"
                                categories={auCategories}
                                colors={AU_TREMOR_COLORS}
                                valueFormatter={v => `${v}%`}
                                yAxisWidth={48}
                                showLegend={false}
                                showGridLines
                                showTooltip={false}
                                connectNulls
                                curveType="monotone"
                            />
                        </div>
                        <div className="mt-4 flex flex-wrap gap-3 justify-center">
                            {auCategories.map((cat, i) => {
                                const code = getActionUnitCode(cat);
                                const friendly = getActionUnitFriendlyName(cat);
                                return (
                                    <CustomTooltip key={cat} tooltip={friendly || code}>
                                        <div className="flex items-center gap-1.5 cursor-help hover:bg-gray-50 px-1 py-1 rounded">
                                            <div className="w-3 h-3 rounded" style={{ backgroundColor: AU_COLORS[i] || '#6b7280' }} />
                                            <span className="text-sm text-gray-700">{code}</span>
                                        </div>
                                    </CustomTooltip>
                                );
                            })}
                        </div>
                    </div>
                );
            })()}
        </div>
    );
}

// ─── Outer card ────────────────────────────────────────────────────────────────

const OUTER_STALE_MS = 10 * 60 * 1000;

export default function QuestionnaireVideoEmotionCard({
    session,
    onStartAnalysis,
    isStarting,
}) {
    const { t } = useTranslation();
    const [activeSegIdx, setActiveSegIdx] = useState(0);

    const segments = session?.segments ?? [];
    const hasCameraRecording = session?.camera_recording_enabled;
    const segmentsWithCamera = segments.filter(s => s.has_person_recording);

    if (!hasCameraRecording || segmentsWithCamera.length === 0) return null;

    const anyStarted = segments.some(s => s.video_emotion_status != null);
    const anyCompleted = segments.some(s => s.video_emotion_status === 'completed');
    const anyFailed = segmentsWithCamera.some(s =>
        ['failed', 'service_unavailable'].includes(s.video_emotion_status)
    );
    const allDone = segmentsWithCamera.every(s =>
        ['completed', 'failed', 'service_unavailable'].includes(s.video_emotion_status)
    );
    const anyActivelyProcessing = segments.some(s =>
        ['queued', 'processing'].includes(s.video_emotion_status)
    );
    const isStale = segmentsWithCamera.some(s =>
        s.video_emotion_status === 'pending_webhook' &&
        s.video_emotion_started_at &&
        Date.now() - new Date(s.video_emotion_started_at).getTime() > OUTER_STALE_MS
    );
    const anyPending = segments.some(s =>
        ['queued', 'processing', 'pending_webhook'].includes(s.video_emotion_status)
    ) && !isStale;

    const showStartBtn = (!anyStarted || isStale || (allDone && anyFailed) || (!anyActivelyProcessing && anyFailed)) && !isStarting;

    // Keep activeSegIdx in bounds when segments change
    const clampedIdx = Math.min(activeSegIdx, segmentsWithCamera.length - 1);
    const activeSeg = segmentsWithCamera[clampedIdx];

    return (
        <Card className="mt-4" onClick={e => e.stopPropagation()}>
            {/* ── Header ── */}
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                <div className="flex items-center gap-2">
                    <FaceSmileIcon className="h-5 w-5 text-blue-500" />
                    <Title className="text-sm font-semibold">{t('Questionnaires.VideoEmotion.Title')}</Title>
                    <CustomTooltip tooltip={t('Questionnaires.VideoEmotion.Tooltip')}>
                        <InformationCircleIcon className="h-4 w-4 text-gray-400 hover:text-gray-600 cursor-help" />
                    </CustomTooltip>
                </div>

                <div className="flex items-center gap-2">
                    {anyPending && !isStale && (
                        <div className="flex items-center gap-1.5 text-xs text-amber-600">
                            <ArrowPathIcon className="h-3.5 w-3.5 animate-spin" />
                            {t('Questionnaires.VideoEmotion.Processing')}
                        </div>
                    )}
                    {isStale && !isStarting && (
                        <span className="text-xs text-red-400">{t('Questionnaires.VideoEmotion.WebhookNotReceived')}</span>
                    )}
                    {showStartBtn && (
                        <button
                            onClick={onStartAnalysis}
                            className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors"
                        >
                            <ChartBarIcon className="h-3.5 w-3.5" />
                            {isStale ? t('Questionnaires.VideoEmotion.ButtonRetryWebhook') : anyFailed ? t('Questionnaires.VideoEmotion.ButtonRetryFailed') : t('Questionnaires.VideoEmotion.ButtonAnalyse')}
                        </button>
                    )}
                    {isStarting && (
                        <button disabled className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-blue-600 text-white opacity-60">
                            <ArrowPathIcon className="h-3.5 w-3.5 animate-spin" />
                            {t('Questionnaires.VideoEmotion.ButtonStarting')}
                        </button>
                    )}
                </div>
            </div>

            {/* ── Segment tabs ── */}
            {segmentsWithCamera.length > 1 && (
                <div className="flex gap-1 mb-4 flex-wrap">
                    {segmentsWithCamera.map((seg, i) => {
                        const originalIdx = segments.indexOf(seg);
                        return (
                            <button
                                key={seg.uuid}
                                onClick={() => setActiveSegIdx(i)}
                                className={`text-xs px-3 py-1.5 rounded border transition-colors ${
                                    clampedIdx === i
                                        ? 'bg-gray-900 text-white border-gray-900'
                                        : 'text-gray-600 border-gray-200 hover:border-gray-400'
                                }`}
                            >
                                Q{originalIdx + 1}
                            </button>
                        );
                    })}
                </div>
            )}

            {/* ── Active segment panel ── */}
            {activeSeg && (
                <SegmentEmotionPanel
                    key={activeSeg.uuid}
                    segment={activeSeg}
                    index={clampedIdx}
                />
            )}
        </Card>
    );
}
